<div class="min-h-screen bg-white px-3 py-6">

  <!-- Page Header -->
  <div class="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-7">
    <div class="lg:col-span-4">
      <div class="w-full rounded-2xl border border-gray-300 bg-gradient-to-r from-gray-50 to-white px-5 py-3 sm:w-fit">
        <div class="border-l-4 border-gray-800 pl-4">
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
            Manage Preview
          </p>

          <h2 class="mt-1 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
            TikTok Video Post
          </h2>
        </div>
      </div>
    </div>

    <div class="flex items-start justify-start lg:col-span-3 lg:justify-end">
      <a
        routerLink="/manage-preview"
        class="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900"
      >
        Back
      </a>
    </div>
  </div>

  <!-- Loading State -->
  <div
    *ngIf="loading"
    class="mx-auto max-w-5xl rounded-2xl border border-gray-300 bg-white p-10 text-center"
  >
    <div class="mx-auto mb-4 h-10 w-10 rounded-full border-4 border-gray-200 border-t-gray-800"></div>

    <p class="text-sm font-medium text-gray-700">
      Loading TikTok preview...
    </p>

    <p class="mt-1 text-xs text-gray-400">
      Please wait while the video preview is being prepared.
    </p>
  </div>

  <!-- Error State -->
  <div
    *ngIf="errorMessage && !loading"
    class="mx-auto max-w-5xl rounded-2xl border border-red-200 bg-red-50 p-6"
  >
    <div class="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-center">
      <div class="md:col-span-10">
        <p class="text-sm font-semibold text-red-800">
          Unable to load preview
        </p>

        <p class="mt-1 text-sm text-red-700">
          {{ errorMessage }}
        </p>
      </div>

      <div class="md:col-span-2 md:text-right">
        <a
          routerLink="/manage-preview"
          class="inline-flex items-center justify-center rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100"
        >
          Go back
        </a>
      </div>
    </div>
  </div>

  <!-- TikTok Preview -->
  <div
    *ngIf="!loading && !errorMessage"
    class="mx-auto flex w-full max-w-4xl justify-center"
  >

    <div class="relative w-full max-w-[540px]">

      <!-- Video Card -->
      <div class="relative overflow-hidden rounded-xl border border-gray-300 bg-black">

        <!-- Video / Poster -->
        <div class="relative aspect-[9/11] min-h-[430px] w-full overflow-hidden bg-black sm:min-h-[520px] lg:min-h-[600px]">

          <video
            *ngIf="videoUrl"
            controls
            [attr.poster]="mainImageUrl"
            class="h-full w-full object-cover"
          >
            <source [src]="videoUrl" type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          <img
            *ngIf="!videoUrl"
            [src]="mainImageUrl"
            alt="TikTok video preview"
            class="h-full w-full object-cover"
          />

          <!-- Dark overlays -->
          <div class="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/45 to-transparent"></div>
          <div class="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/80 via-black/35 to-transparent"></div>

          <!-- Mute Icon -->
          <button
            type="button"
            class="absolute left-3 top-3 z-20 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/20 text-white backdrop-blur-sm transition hover:bg-black/35"
          >
            <mat-icon class="text-[18px]">volume_off</mat-icon>
          </button>

          <!-- Caption -->
          <div class="absolute inset-x-0 bottom-0 z-20 px-4 pb-4 pr-16">
            <div class="flex items-center gap-1.5">
              <p class="mb-0 text-sm font-bold leading-none text-white">
                {{ postFeed }}
              </p>

              <span class="inline-flex h-4 w-4 items-center justify-center rounded-full bg-sky-500 text-[10px] font-bold text-white">
                ✓
              </span>
            </div>

            <p class="mt-2 line-clamp-2 text-xs font-semibold leading-5 text-white">
              {{ postDescription }}
            </p>
          </div>

          <!-- Bottom Progress Bar -->
          <div class="absolute inset-x-0 bottom-0 z-30 h-1 bg-white/30">
            <div class="h-full w-[58%] bg-red-500"></div>
          </div>

        </div>
      </div>

      <!-- Right TikTok Actions -->
      <div class="absolute -right-10 bottom-3 z-30 hidden flex-col items-center gap-3 sm:flex lg:-right-12">

        <!-- Profile Avatar -->
        <div class="relative">
          <img
            [src]="thumbnailFilePath"
            class="h-9 w-9 rounded-full border border-gray-300 bg-white object-cover"
            alt="TikTok profile"
          />

          <span class="absolute -bottom-2 left-1/2 inline-flex h-4 w-4 -translate-x-1/2 items-center justify-center rounded-full bg-pink-500 text-[10px] font-bold leading-none text-white">
            +
          </span>
        </div>

        <!-- Like -->
        <button
          type="button"
          class="flex flex-col items-center gap-1 text-gray-900"
        >
          <span class="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-100">
            <mat-icon class="text-[21px]">favorite</mat-icon>
          </span>

          <span class="text-[11px] font-bold">
            {{ likesCount }}
          </span>
        </button>

        <!-- Comments -->
        <button
          type="button"
          class="flex flex-col items-center gap-1 text-gray-900"
        >
          <span class="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-100">
            <mat-icon class="text-[20px]">chat_bubble</mat-icon>
          </span>

          <span class="text-[11px] font-bold">
            {{ commentsCount }}
          </span>
        </button>

        <!-- Saved -->
        <button
          type="button"
          class="flex flex-col items-center gap-1 text-gray-900"
        >
          <span class="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-100">
            <mat-icon class="text-[21px]">bookmark</mat-icon>
          </span>

          <span class="text-[11px] font-bold">
            {{ savesCount }}
          </span>
        </button>

        <!-- Share -->
        <button
          type="button"
          class="flex flex-col items-center gap-1 text-gray-900"
        >
          <span class="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-100">
            <mat-icon class="text-[21px]">reply</mat-icon>
          </span>

          <span class="text-[11px] font-bold">
            {{ sharesCount }}
          </span>
        </button>

        <!-- Bottom Icon -->
        <button
          type="button"
          class="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black text-white"
        >
          <mat-icon class="text-[20px]">music_note</mat-icon>
        </button>

      </div>

      <!-- Mobile Actions Under Video -->
      <div class="mt-4 grid grid-cols-4 gap-2 sm:hidden">
        <button
          type="button"
          class="flex items-center justify-center gap-1 rounded-2xl border border-gray-300 bg-white px-2 py-2.5 text-xs font-semibold text-gray-900"
        >
          <mat-icon class="text-[18px]">favorite</mat-icon>
          {{ likesCount }}
        </button>

        <button
          type="button"
          class="flex items-center justify-center gap-1 rounded-2xl border border-gray-300 bg-white px-2 py-2.5 text-xs font-semibold text-gray-900"
        >
          <mat-icon class="text-[18px]">chat_bubble</mat-icon>
          {{ commentsCount }}
        </button>

        <button
          type="button"
          class="flex items-center justify-center gap-1 rounded-2xl border border-gray-300 bg-white px-2 py-2.5 text-xs font-semibold text-gray-900"
        >
          <mat-icon class="text-[18px]">bookmark</mat-icon>
          {{ savesCount }}
        </button>

        <button
          type="button"
          class="flex items-center justify-center gap-1 rounded-2xl border border-gray-300 bg-white px-2 py-2.5 text-xs font-semibold text-gray-900"
        >
          <mat-icon class="text-[18px]">reply</mat-icon>
          {{ sharesCount }}
        </button>
      </div>

    </div>

  </div>

</div>